import React, { ReactElement, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { useForm } from '@tanstack/react-form'
import { useAppDispatch } from 'src/store/hooks'
import {
  createPendingANCCEvent,
  createPendingANCCCourse,
  fetchEventAccreditations,
  fetchCourseAccreditations,
} from 'src/v3/content-pipeline/accreditation.slice'
import { modalPaperProps } from 'src/v3/utilities/modal-styles'
import { AccreditationStatus } from '../../shared-types'
import { StatusDropdownButton } from '../shared'
import {
  CourseCodeField,
  ExpirationDateField,
  TargetAudienceField,
  EducationalNeedsField,
  CompetencyField,
  ProfessionalSourceField,
  NotesField,
  FieldsRow,
} from './form-fields'

type CreateFormProps = {
  eventId?: string
  courseId?: string
  open: boolean
  setOpen: (isOpen: boolean) => void
  onSuccess: () => void
}

export let CreateForm = ({
  eventId,
  courseId,
  open,
  setOpen,
  onSuccess,
}: CreateFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [selectedStatus, setSelectedStatus] =
    useState<AccreditationStatus>('Draft')

  let form = useForm({
    defaultValues: {
      courseCode: '',
      expirationDate: '',
      targetAudience: [] as string[],
      educationalNeeds: [] as string[],
      competency: '',
      professionalSource: '',
      notes: '',
    },
    onSubmit: ({ value }) => {
      if (eventId) {
        dispatch(
          createPendingANCCEvent({
            eventId,
            status: selectedStatus,
            courseCode: value.courseCode || null,
            expirationDate: value.expirationDate || null,
            targetAudience: value.targetAudience,
            educationalNeeds: value.educationalNeeds,
            competency: value.competency || null,
            professionalSource: value.professionalSource || null,
            notes: value.notes || null,
          }),
        )
        dispatch(fetchEventAccreditations(eventId))
      } else if (courseId) {
        dispatch(
          createPendingANCCCourse({
            courseId,
            status: selectedStatus,
            courseCode: value.courseCode || null,
            expirationDate: value.expirationDate || null,
            targetAudience: value.targetAudience,
            educationalNeeds: value.educationalNeeds,
            competency: value.competency || null,
            professionalSource: value.professionalSource || null,
            notes: value.notes || null,
          }),
        )
        dispatch(fetchCourseAccreditations(courseId))
      }
      form.reset()
      setSelectedStatus('Draft')
      setOpen(false)
      onSuccess()
    },
  })

  return (
    <Dialog
      open={open}
      onClose={() => {
        form.reset()
        setOpen(false)
      }}
      fullWidth
      slotProps={{
        paper: {
          ...modalPaperProps,
          sx: { ...modalPaperProps.sx, borderRadius: 2 },
        },
      }}
      maxWidth="md"
    >
      <DialogTitle>Create Pending ANCC Accreditation</DialogTitle>
      <DialogContent>
        <Box className="grid gap-6 mt-2">
          <Typography color="text.secondary" variant="body2">
            Create a pending ANCC accreditation for review. This will be
            submitted for approval before being finalized.
          </Typography>

          <form.Field
            name="courseCode"
            children={(field) => <CourseCodeField field={field} />}
          />

          <form.Field
            name="expirationDate"
            children={(field) => <ExpirationDateField field={field} />}
          />

          <FieldsRow>
            <form.Field
              name={'targetAudience' as any}
              children={(field) => <TargetAudienceField field={field} />}
            />
            <form.Field
              name={'educationalNeeds' as any}
              children={(field) => <EducationalNeedsField field={field} />}
            />
          </FieldsRow>

          <form.Field
            name="competency"
            children={(field) => <CompetencyField field={field} />}
          />

          <form.Field
            name="professionalSource"
            children={(field) => <ProfessionalSourceField field={field} />}
          />

          <form.Field
            name="notes"
            children={(field) => <NotesField field={field} />}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            form.reset()
            setSelectedStatus('Draft')
            setOpen(false)
          }}
        >
          Cancel
        </Button>
        <StatusDropdownButton
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          onSubmit={() => form.handleSubmit()}
        />
      </DialogActions>
    </Dialog>
  )
}
